// src/renderer.tsx
import React, { createContext, useContext } from "react";
var RendererContext = createContext({});
function RenderContextProvider(props) {
  const overrideBlocks = props.blocks;
  const blocks = Object.keys(BLOCK_RENDERERS).reduce((prev, type) => {
    const blockType = type;
    const renderer = overrideBlocks?.[blockType] || BLOCK_RENDERERS[blockType];
    prev[blockType] = renderer;
    return prev;
  }, {});
  const overrideDecorators = props.decorators;
  const decorators = Object.keys(DECORATOR_RENDERERS).reduce((prev, type) => {
    const decoratorType = type;
    prev[decoratorType] = overrideDecorators?.[decoratorType] || DECORATOR_RENDERERS[decoratorType];
    return prev;
  }, {});
  const value = { blocks, decorators, components: props.components };
  return /* @__PURE__ */ React.createElement(RendererContext.Provider, { value }, props.children);
}
function useBlocks() {
  const value = useContext(RendererContext);
  return value.blocks || BLOCK_RENDERERS;
}
function useDecorators() {
  const value = useContext(RendererContext);
  return value.decorators || DECORATOR_RENDERERS;
}
function useComponents() {
  const value = useContext(RendererContext);
  return value.components || {};
}
function RenderBlock(props) {
  const blocks = useBlocks();
  const Component2 = blocks[props.block.type];
  return /* @__PURE__ */ React.createElement(Component2, { block: props.block });
}
function RenderBlocks(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.blocks.map((block) => /* @__PURE__ */ React.createElement(RenderBlock, { block, key: block.id })));
}
function RenderContents(props) {
  return props.contents ? props.contents : props.fallback;
}
function RenderChildren(props) {
  const isArray = Array.isArray(props.block?.value);
  const isString = typeof props.block?.value === "string";
  const render = () => {
    if (isArray) {
      return /* @__PURE__ */ React.createElement(RenderBlocks, { blocks: props.block.value });
    } else if (isString) {
      return /* @__PURE__ */ React.createElement(RenderText, { text: props.block.value });
    } else {
      return /* @__PURE__ */ React.createElement(RenderText, { text: "" });
    }
  };
  return render();
}
function RenderText(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.text);
}
function Renderer(props) {
  return /* @__PURE__ */ React.createElement(RenderBlocks, { blocks: props.data });
}
function getAttributes(props, extra = {}) {
  const { block, ...rest } = props;
  let { children, decorator, otherDecorators, ...attributes } = rest;
  attributes = {
    id: block?.properties?.id,
    ...extra,
    ...attributes
  };
  return attributes;
}
function WithCaption(props) {
  return !!props.caption ? /* @__PURE__ */ React.createElement("figure", null, props.children, /* @__PURE__ */ React.createElement("figcaption", null, props.caption)) : props.children || null;
}
function RenderBlockChildrenFactory() {
  return function(props) {
    return /* @__PURE__ */ React.createElement(RenderChildren, { block: props.block });
  };
}
function EmptyChildrenFactory() {
  return function(props) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null);
  };
}
function Anchor(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("a", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Anchor.Children, { block: props.block }) }));
}
Anchor.Children = RenderBlockChildrenFactory();
function Code(props) {
  const attributes = getAttributes(props, {
    "data-language": props.block?.value?.language
  });
  const codeAttributes = getAttributes(props, {
    className: `language-${props.block?.value?.language}`
  });
  return /* @__PURE__ */ React.createElement("pre", { ...attributes }, /* @__PURE__ */ React.createElement("code", { ...codeAttributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Code.Children, { block: props.block }) })));
}
Code.Children = function(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.block?.value?.code);
};
function CodeWithCaption(props) {
  return /* @__PURE__ */ React.createElement(WithCaption, { caption: props.block?.value?.caption }, /* @__PURE__ */ React.createElement(Code, { ...props }));
}
function Component(props) {
  const component = props?.block.properties?.component;
  const components = useComponents();
  const ComponentElement = !!component ? components?.[component] : void 0;
  const value = props.block.value ? JSON.stringify(props.block.value) : "";
  const attributes = getAttributes(props, {
    className: "component",
    "data-component": props.block.properties?.component,
    "data-component-value": value
  });
  return !!ComponentElement ? /* @__PURE__ */ React.createElement(ComponentElement, { ...props }) : /* @__PURE__ */ React.createElement("div", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Component.Children, { block: props.block }) }));
}
Component.Children = function(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, "Component: ", props.block?.properties?.component);
};
function Divider(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("hr", { ...attributes });
}
Divider.Children = EmptyChildrenFactory();
function Fragment(props) {
  const hasDecorators = !!props.block?.properties?.decorators?.length;
  const decorators = props.block?.properties?.decorators;
  return hasDecorators ? /* @__PURE__ */ React.createElement(Decorators, { block: props.block, decorators }) : /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Fragment.Children, { block: props.block }) });
}
Fragment.Children = RenderBlockChildrenFactory();
function Heading(props) {
  const attributes = getAttributes(props);
  const render = () => {
    switch (props?.block?.properties?.level) {
      case 2: {
        return /* @__PURE__ */ React.createElement("h2", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
      case 3: {
        return /* @__PURE__ */ React.createElement("h3", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
      case 4: {
        return /* @__PURE__ */ React.createElement("h4", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
      case 5: {
        return /* @__PURE__ */ React.createElement("h5", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
      case 6: {
        return /* @__PURE__ */ React.createElement("h6", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
      default: {
        return /* @__PURE__ */ React.createElement("h1", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Heading.Children, { block: props.block }) }));
      }
    }
  };
  return render();
}
Heading.Children = RenderBlockChildrenFactory();
function Image(props) {
  const src = props.block?.value?.asset?.sys?.uri;
  const attributes = getAttributes(props, {
    src,
    alt: props.block?.value?.altText,
    title: props?.block?.value?.caption
  });
  return /* @__PURE__ */ React.createElement("img", { ...attributes });
}
Image.Children = EmptyChildrenFactory();
function ImageWithCaption(props) {
  return /* @__PURE__ */ React.createElement(WithCaption, { caption: props.block?.value?.caption }, /* @__PURE__ */ React.createElement(Image, { ...props }));
}
function InlineEntry(props) {
  const href = props?.block?.value?.sys?.uri;
  const attributes = getAttributes(props, {
    href
  });
  return !!attributes.href ? /* @__PURE__ */ React.createElement("a", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(InlineEntry.Children, { block: props.block }) })) : /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(InlineEntry.Children, { block: props.block }) });
}
InlineEntry.Children = function(props) {
  const entryTitle = props?.block?.value?.entryTitle || "";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, entryTitle);
};
function Link(props) {
  const linkValue = props?.block?.properties?.link;
  const attributes = getAttributes(props, {
    href: linkValue?.sys?.uri,
    target: props?.block?.properties?.newTab ? "_blank" : null,
    rel: props?.block?.properties?.newTab ? "noopener noreferrer" : null
  });
  return !!attributes.href ? /* @__PURE__ */ React.createElement("a", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Link.Children, { block: props.block }) })) : /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Link.Children, { block: props.block }) });
}
Link.Children = RenderBlockChildrenFactory();
function List(props) {
  const isOrdered = props.block?.properties?.listType === "ordered";
  const attributes = getAttributes(props, {
    start: isOrdered ? props.block?.properties?.start : null
  });
  return isOrdered ? /* @__PURE__ */ React.createElement("ol", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(List.Children, { block: props.block }) })) : /* @__PURE__ */ React.createElement("ul", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(List.Children, { block: props.block }) }));
}
List.Children = RenderBlockChildrenFactory();
function ListItem(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("li", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(ListItem.Children, { block: props.block }) }));
}
ListItem.Children = RenderBlockChildrenFactory();
function Panel(props) {
  const attributes = getAttributes(props, {
    className: ["panel", props.block?.properties?.panelType || "info"].join(" ")
  });
  return /* @__PURE__ */ React.createElement("aside", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Panel.Children, { block: props.block }) }));
}
Panel.Children = RenderBlockChildrenFactory();
function Paragraph(props) {
  const attributes = getAttributes(props, {
    className: props.block?.properties?.paragraphType
  });
  return /* @__PURE__ */ React.createElement("p", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Paragraph.Children, { block: props.block }) }));
}
Paragraph.Children = RenderBlockChildrenFactory();
function Quote(props) {
  const attributes = getAttributes(props, {
    "cite": props.block?.properties?.url
  });
  return /* @__PURE__ */ React.createElement("blockquote", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Quote.Children, { block: props.block }) }));
}
Quote.Children = function(props) {
  const source = props.block?.properties?.source;
  const citation = props.block?.properties?.citation;
  const hasChildren = !!source || !!citation;
  return hasChildren ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(RenderChildren, { block: props.block })), /* @__PURE__ */ React.createElement("footer", null, source, " ", !!citation ? /* @__PURE__ */ React.createElement("cite", null, citation) : /* @__PURE__ */ React.createElement(React.Fragment, null))) : /* @__PURE__ */ React.createElement(RenderChildren, { block: props.block });
};
function Table(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("table", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Table.Children, { block: props.block }) }));
}
Table.Children = RenderBlockChildrenFactory();
function TableBody(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("tbody", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableBody.Children, { block: props.block }) }));
}
TableBody.Children = RenderBlockChildrenFactory();
function TableCaption(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("caption", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableCaption.Children, { block: props.block }) }));
}
TableCaption.Children = RenderBlockChildrenFactory();
function TableCell(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("td", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableCell.Children, { block: props.block }) }));
}
TableCell.Children = RenderBlockChildrenFactory();
function TableFooter(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("tfoot", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableFooter.Children, { block: props.block }) }));
}
TableFooter.Children = RenderBlockChildrenFactory();
function TableHeader(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("thead", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableHeader.Children, { block: props.block }) }));
}
TableHeader.Children = RenderBlockChildrenFactory();
function TableHeaderCell(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("th", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableHeaderCell.Children, { block: props.block }) }));
}
TableHeaderCell.Children = RenderBlockChildrenFactory();
function TableRow(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("tr", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(TableRow.Children, { block: props.block }) }));
}
TableRow.Children = RenderBlockChildrenFactory();
function Decorators(props) {
  const decorators = useDecorators();
  const remainingDecorators = !!props.decorators ? [...props.decorators] : void 0;
  const firstDecorator = !!remainingDecorators ? remainingDecorators.shift() : void 0;
  const DecoratorComponent = !!firstDecorator ? decorators[firstDecorator] : void 0;
  const render = () => {
    if (!!DecoratorComponent) {
      return /* @__PURE__ */ React.createElement(DecoratorComponent, { block: props.block, decorator: firstDecorator, otherDecorators: remainingDecorators });
    } else if (firstDecorator) {
      return /* @__PURE__ */ React.createElement(Decorators, { block: props.block, decorators: remainingDecorators });
    } else {
      return /* @__PURE__ */ React.createElement(Fragment.Children, { block: props.block });
    }
  };
  return render();
}
function DecoratorChildren(props) {
  return /* @__PURE__ */ React.createElement(Decorators, { block: props.block, decorators: props.otherDecorators });
}
function InlineCode(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("code", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(InlineCode.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
InlineCode.Children = DecoratorChildren;
function Delete(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("del", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Delete.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Delete.Children = DecoratorChildren;
function Emphasis(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("em", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Emphasis.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Emphasis.Children = DecoratorChildren;
function Insert(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("ins", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Insert.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Insert.Children = DecoratorChildren;
function Keyboard(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("kbd", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Keyboard.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Keyboard.Children = DecoratorChildren;
function LineBreak(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("br", { ...attributes });
}
LineBreak.Children = function(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null);
};
function Mark(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("mark", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Mark.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Mark.Children = DecoratorChildren;
function Strong(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("strong", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Strong.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Strong.Children = DecoratorChildren;
function Strikethrough(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("s", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Strikethrough.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Strikethrough.Children = DecoratorChildren;
function Subscript(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("sub", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Subscript.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Subscript.Children = DecoratorChildren;
function Superscript(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("sup", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Superscript.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Superscript.Children = DecoratorChildren;
function Underline(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("u", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Underline.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Underline.Children = DecoratorChildren;
function Variable(props) {
  const attributes = getAttributes(props);
  return /* @__PURE__ */ React.createElement("var", { ...attributes }, /* @__PURE__ */ React.createElement(RenderContents, { contents: props.children, fallback: /* @__PURE__ */ React.createElement(Variable.Children, { block: props.block, decorator: props.decorator, otherDecorators: props.otherDecorators }) }));
}
Variable.Children = DecoratorChildren;
var BLOCK_RENDERERS = {
  "_anchor": Anchor,
  "_code": CodeWithCaption,
  "_component": Component,
  "_divider": Divider,
  "_fragment": Fragment,
  "_heading": Heading,
  "_image": ImageWithCaption,
  "_inlineEntry": InlineEntry,
  "_link": Link,
  "_list": List,
  "_listItem": ListItem,
  "_panel": Panel,
  "_paragraph": Paragraph,
  "_quote": Quote,
  "_table": Table,
  "_tableBody": TableBody,
  "_tableCaption": TableCaption,
  "_tableCell": TableCell,
  "_tableFooter": TableFooter,
  "_tableHeader": TableHeader,
  "_tableHeaderCell": TableHeaderCell,
  "_tableRow": TableRow
};
var DECORATOR_RENDERERS = {
  "code": InlineCode,
  "delete": Delete,
  "emphasis": Emphasis,
  "insert": Insert,
  "keyboard": Keyboard,
  "linebreak": LineBreak,
  "mark": Mark,
  "strikethrough": Strikethrough,
  "strong": Strong,
  "subscript": Subscript,
  "superscript": Superscript,
  "underline": Underline,
  "variable": Variable
};
export {
  Anchor,
  Code,
  Component,
  Delete,
  Divider,
  Emphasis,
  Fragment,
  Heading,
  Image,
  InlineCode,
  InlineEntry,
  Insert,
  Keyboard,
  LineBreak,
  Link,
  List,
  ListItem,
  Mark,
  Panel,
  Paragraph,
  Quote,
  RenderContextProvider,
  Renderer,
  RendererContext,
  Strikethrough,
  Strong,
  Subscript,
  Superscript,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Underline,
  Variable
};
//# sourceMappingURL=canvas-react.mjs.map